import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Loader2, CreditCard, AlertCircle, Lock } from "lucide-react";
import { api, apiErr } from "@/lib/api";

const money = (n, cur = "CAD") => `${cur === "CAD" ? "CA" : ""}$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Shell({ company, children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col items-center px-4 py-8 sm:py-14">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6" data-testid="pay-company">
          {company?.logo_url?.startsWith("https://") ? <img src={company.logo_url} alt="" className="h-10 w-auto" /> : <img src="/logo.png" alt="" className="h-10 w-auto" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
          <div>
            <div className="font-head font-extrabold text-slate-900 leading-tight">{company?.name || "Secure payment"}</div>
            {company?.rcic && <div className="text-[11px] text-slate-500">{company.rcic}</div>}
          </div>
        </div>
        {children}
        <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Secure payment · your card details are entered only on Stripe or PayPal</div>
      </div>
    </div>
  );
}

export default function PublicPay() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);   // confirmation after returning from Stripe/PayPal
  const [confirming, setConfirming] = useState(false);
  const tries = useRef(0);
  const [choice, setChoice] = useState("full");   // full | deposit | custom
  const [custom, setCustom] = useState("");

  const load = () => api.get(`/public/pay/${token}`).then((r) => { setInfo(r.data); if (r.data?.choices?.instalment) setChoice((c) => (c === "full" ? "instalment" : c)); }).catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  // Returning from checkout: confirm with the provider (polls briefly if it's still processing)
  useEffect(() => {
    const provider = params.get("provider");
    const session_id = params.get("session_id");
    const order_id = params.get("token");   // PayPal appends ?token=<ORDER_ID>
    if (!provider || (!session_id && !order_id)) return;
    setConfirming(true);
    const run = async () => {
      try {
        const { data } = await api.post(`/public/pay/${token}/confirm`, { provider, session_id, order_id });
        if (data.status === "paid") { setResult(data); setConfirming(false); load(); return; }
        if (tries.current++ < 10) { setTimeout(run, 2500); return; }
        setConfirming(false); setErr("We couldn't confirm your payment yet. If you were charged, the office will see it shortly — please don't pay again.");
      } catch (e) { setConfirming(false); setErr(apiErr(e)); }
    };
    run();
  }, []); // eslint-disable-line

  const ch = info?.choices || {};
  const due = Number(info?.amount_due || 0);
  const payAmount = choice === "instalment" && ch.instalment ? ch.instalment.amount : choice === "deposit" && ch.deposit ? ch.deposit : choice === "custom" ? Math.round(Number(custom || 0) * 100) / 100 : due;
  const customErr = choice === "custom" && (!(payAmount > 0) ? "Enter an amount" : payAmount > due + 0.009 ? "That's more than you owe" : payAmount < (ch.min_amount || 0) - 0.009 ? `Minimum is ${money(ch.min_amount, info?.currency)}` : "");

  const pay = async (provider) => {
    if (customErr) { setErr(customErr); return; }
    setBusy(provider);
    try {
      const { data } = await api.post(`/public/pay/${token}/checkout`, { provider, origin_url: window.location.origin, amount: payAmount });
      if (data.redirect_url) window.location.href = data.redirect_url;
      else { setBusy(""); setErr("Couldn't start the payment. Please try again."); }
    } catch (e) { setBusy(""); setErr(apiErr(e)); }
  };

  if (!info && !err) return <Shell><div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></Shell>;
  if (!info) return (
    <Shell><div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 text-center" data-testid="pay-error">
      <AlertCircle className="h-10 w-10 text-rose-500 mx-auto" /><p className="mt-3 text-slate-700 font-semibold">{err}</p>
    </div></Shell>
  );

  const cur = info.currency;
  const canceled = params.get("canceled") === "1";
  const card = "bg-white rounded-3xl shadow-sm border border-slate-200 p-6 sm:p-8";

  if (confirming) return (
    <Shell company={info.company}><div className={`${card} text-center`} data-testid="pay-confirming">
      <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
      <p className="mt-4 font-semibold text-slate-800">Confirming your payment…</p>
      <p className="text-sm text-slate-500 mt-1">Please keep this page open for a moment.</p>
    </div></Shell>
  );

  if (result || info.paid) {
    const r = result || info.last_payment;
    return (
      <Shell company={info.company}><div className={`${card} text-center`} data-testid="pay-success">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
        <h1 className="font-head text-2xl font-extrabold text-slate-900 mt-3">Paid — thank you!</h1>
        <p className="text-sm text-slate-500 mt-1">{info.title}{info.number ? ` · ${info.number}` : ""}</p>
        {r && (
          <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1.5">
            <div className="flex justify-between"><span className="text-slate-500">Amount paid</span><b data-testid="pay-success-amount">{money(r.amount, cur)}</b></div>
            {r.fee > 0 && <div className="flex justify-between"><span className="text-slate-500">Processing fee</span><span>{money(r.fee, cur)}</span></div>}
            {r.reference && <div className="flex justify-between gap-3"><span className="text-slate-500">Reference</span><span className="font-mono text-xs break-all text-right" data-testid="pay-success-ref">{r.reference}</span></div>}
            <div className="flex justify-between"><span className="text-slate-500">Paid with</span><span>{r.provider === "stripe" ? "Card (Stripe)" : "PayPal"}</span></div>
          </div>
        )}
        {result && info.amount_due > 0 && <p className="text-sm text-slate-600 mt-3" data-testid="pay-success-remaining">Remaining balance: <b>{money(info.amount_due, cur)}</b> — you can pay the rest anytime from this same link.</p>}
        <p className="text-xs text-slate-400 mt-4">The office has been notified{result ? " and your receipt will be emailed to you" : ""}. You can close this page.</p>
      </div></Shell>
    );
  }

  return (
    <Shell company={info.company}>
      <div className={card} data-testid="pay-card">
        <div className="text-sm text-slate-500">Hi {(info.client_name || "").split(" ")[0] || "there"},</div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-4">Amount owing</div>
        <div className="font-head text-4xl font-extrabold text-slate-900 mt-1" data-testid="pay-amount-due">{money(info.amount_due, cur)}</div>
        <div className="text-sm text-slate-600 mt-1" data-testid="pay-title">{info.title}{info.number ? <span className="text-slate-400 font-mono text-xs"> · {info.number}</span> : null}</div>

        {canceled && <div className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="pay-canceled">Payment was cancelled — nothing was charged. You can try again below.</div>}
        {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="pay-inline-error">{err}</div>}

        {!info.active ? (
          <div className="mt-6 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="pay-inactive">This payment link is no longer active. Please contact the office{info.company?.phone ? ` at ${info.company.phone}` : ""} for a new link.</div>
        ) : info.providers.length === 0 ? (
          <div className="mt-6 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="pay-unavailable">Online payment isn't available right now — please contact the office{info.company?.phone ? ` at ${info.company.phone}` : ""}.</div>
        ) : (
          <div className="mt-6 space-y-3">
            {(ch.allow_partial || ch.instalment) && (
              <div className="space-y-2" data-testid="pay-choices">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">How much would you like to pay?</div>
                {[...(ch.instalment ? [["instalment", `Instalment ${ch.instalment.n} of ${ch.instalment.count}${ch.instalment.overdue ? " (overdue)" : ` · due ${new Date(`${ch.instalment.due_date}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}`, money(ch.instalment.amount, cur)]] : []), ["full", "Full balance", money(due, cur)], ...(ch.allow_partial && ch.deposit ? [["deposit", `Deposit (${ch.deposit_percent}% of fees)`, money(ch.deposit, cur)]] : []), ...(ch.allow_partial ? [["custom", "Another amount", `min ${money(ch.min_amount, cur)}`]] : [])].map(([k, l, v]) => (
                  <label key={k} data-testid={`pay-choice-${k}`} className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 cursor-pointer ${choice === k ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                    <input type="radio" name="amt" checked={choice === k} onChange={() => { setChoice(k); setErr(""); }} className="accent-primary" />
                    <span className="flex-1 text-sm font-semibold text-slate-800">{l}</span><span className="text-sm text-slate-500">{v}</span>
                  </label>
                ))}
                {choice === "custom" && (
                  <div>
                    <input data-testid="pay-custom-amount" type="number" inputMode="decimal" min={ch.min_amount} max={due} step="0.01" autoFocus value={custom} onChange={(e) => { setCustom(e.target.value); setErr(""); }}
                      placeholder={`Amount (${money(ch.min_amount, cur)} – ${money(due, cur)})`} className="w-full px-3.5 py-3 rounded-xl border border-slate-300 text-base" />
                    {custom && customErr && <div className="text-xs text-rose-600 mt-1" data-testid="pay-custom-error">{customErr}</div>}
                  </div>
                )}
              </div>
            )}
            {info.providers.map((p) => (
              <div key={p.id} className="rounded-2xl border border-slate-200 p-4" data-testid={`pay-option-${p.id}`}>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between text-slate-600"><span>{payAmount < due - 0.009 ? "Paying now" : "Balance"}</span><span data-testid={`pay-base-${p.id}`}>{money(payAmount || 0, cur)}</span></div>
                  {p.fee_percent > 0 && <div className="flex justify-between text-slate-600"><span>{p.fee_label} ({p.fee_percent}%)</span><span data-testid={`pay-fee-${p.id}`}>{money(Math.round((payAmount || 0) * p.fee_percent) / 100, cur)}</span></div>}
                  <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-100"><span>Total</span><span data-testid={`pay-total-${p.id}`}>{money((payAmount || 0) + Math.round((payAmount || 0) * p.fee_percent) / 100, cur)}</span></div>
                </div>
                <button type="button" data-testid={`pay-btn-${p.id}`} disabled={!!busy || !!customErr} onClick={() => pay(p.id)}
                  className={`mt-3 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-base transition-[filter] disabled:opacity-60 ${p.id === "stripe" ? "bg-secondary text-white hover:brightness-110" : "bg-[#FFC439] text-[#003087] hover:brightness-95"}`}>
                  {busy === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : p.id === "stripe" ? <CreditCard className="h-5 w-5" /> : null}
                  {p.id === "stripe" ? "Pay by card" : <span>Pay with <b className="italic">PayPal</b></span>}
                </button>
                {p.test_mode && <div className="text-[10px] text-amber-600 text-center mt-1.5">Test mode — no real money is charged</div>}
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-500" /> You'll pay on Stripe's or PayPal's secure page, then return here.</div>
      </div>
    </Shell>
  );
}
